<div class="landing-builder">

  <!-- ── Step 1: template picker ──────────────────────────────────────────── -->
  @if (step === 'templates') {
    <header class="landing-builder-toolbar">
      <button type="button" mat-stroked-button class="lb-button" (click)="backToLayoutBuilder()" matTooltip="Back to the dashboard builder">
        <mat-icon>arrow_back</mat-icon> Builder
      </button>
      <div class="lb-toolbar-title">
        <span class="lb-kicker">Landing Page</span>
        <h2>Choose a template</h2>
      </div>
    </header>

    @if (isLoadingExisting) {
      <div class="lb-state">Loading landing page…</div>
    } @else {
      <div class="lb-template-grid">
        @for (template of templates; track template.id) {
          <section class="lb-template-card">
            <div class="lb-template-head">
              <mat-icon>web</mat-icon>
              <h3>{{ template.name }}</h3>
            </div>
            <p class="lb-template-description">{{ template.description }}</p>
            <div class="lb-template-preview">
              <app-dvm-landing-page [config]="template.config" [interactive]="false"></app-dvm-landing-page>
            </div>
            <button type="button" mat-flat-button color="primary" class="lb-button lb-use-button" (click)="useTemplate(template)">
              <mat-icon>check</mat-icon> Use this template
            </button>
          </section>
        }
      </div>
    }
  }

  <!-- ── Step 2: editor ───────────────────────────────────────────────────── -->
  @if (step === 'edit' && config; as cfg) {
    <header class="landing-builder-toolbar">
      <button type="button" mat-stroked-button class="lb-button" (click)="backToTemplates()" matTooltip="Pick a different template">
        <mat-icon>arrow_back</mat-icon> Templates
      </button>
      <div class="lb-toolbar-title">
        <span class="lb-kicker">Landing Page</span>
        <input class="lb-name-input" [(ngModel)]="pageName" placeholder="Landing page name" maxlength="120" />
      </div>
      <span class="lb-toolbar-spacer"></span>
      <button type="button" mat-flat-button color="primary" class="lb-button lb-publish-button" [disabled]="!canPublish" (click)="publish()" matTooltip="Publish — it appears first in the dashboards menu">
        <mat-icon>publish</mat-icon> {{ isPublishing ? 'Publishing…' : 'Save & Publish' }}
      </button>
    </header>

    <div class="lb-editor">
      <!-- Left: config form -->
      <aside class="lb-form">
        <section class="lb-form-section">
          <h3 class="lb-form-heading">Header</h3>
          <label class="lb-field">
            <span>Title</span>
            <input [(ngModel)]="cfg.header.title" placeholder="Page title" />
          </label>
          <label class="lb-field">
            <span>Welcome text</span>
            <textarea rows="3" [(ngModel)]="cfg.header.subtitle" placeholder="Short welcome / description"></textarea>
          </label>
          <div class="lb-field-row">
            <label class="lb-field">
              <span>Icon <em>(material icon name)</em></span>
              <input [(ngModel)]="cfg.header.icon" placeholder="e.g. star" />
            </label>
            <label class="lb-field">
              <span>Section label</span>
              <input [(ngModel)]="cfg.sectionLabel" placeholder="SELECT MODULE" />
            </label>
          </div>
          <label class="lb-field lb-color-field">
            <span>Accent color</span>
            <span class="lb-color-inputs">
              <input type="color" [(ngModel)]="cfg.accent" />
              <input class="lb-color-text" [(ngModel)]="cfg.accent" placeholder="#c8102e" />
            </span>
          </label>
        </section>

        <section class="lb-form-section">
          <div class="lb-form-heading-row">
            <h3 class="lb-form-heading">Modules</h3>
            <button type="button" mat-stroked-button class="lb-button lb-small-button" (click)="addModule()">
              <mat-icon>add</mat-icon> Add module
            </button>
          </div>

          @for (module of cfg.modules; track module.id; let i = $index) {
            <details class="lb-module" [id]="'landing-module-form-' + i" [open]="i === activeModuleIndex">
              <summary>
                <span class="lb-module-summary-title">{{ module.title || 'Module ' + (i + 1) }}</span>
                <span class="lb-module-summary-actions" (click)="$event.preventDefault(); $event.stopPropagation()">
                  <button type="button" mat-icon-button [disabled]="i === 0" (click)="moveModule(i, -1)" matTooltip="Move up"><mat-icon>arrow_upward</mat-icon></button>
                  <button type="button" mat-icon-button [disabled]="i === cfg.modules.length - 1" (click)="moveModule(i, 1)" matTooltip="Move down"><mat-icon>arrow_downward</mat-icon></button>
                  <button type="button" mat-icon-button class="lb-danger" (click)="removeModule(i)" matTooltip="Remove module"><mat-icon>delete_outline</mat-icon></button>
                </span>
              </summary>

              <div class="lb-field-row">
                <label class="lb-field">
                  <span>Badge</span>
                  <input [(ngModel)]="module.badge" placeholder="MODULE 01" />
                </label>
                <label class="lb-field">
                  <span>Icon</span>
                  <input [(ngModel)]="module.icon" placeholder="e.g. trending_up" />
                </label>
              </div>
              <label class="lb-field">
                <span>Title</span>
                <input [(ngModel)]="module.title" placeholder="Module title" />
              </label>
              <label class="lb-field">
                <span>Description</span>
                <textarea rows="2" [(ngModel)]="module.description" placeholder="What this module offers"></textarea>
              </label>
              <label class="lb-field lb-color-field">
                <span>Accent <em>(optional — overrides page accent)</em></span>
                <span class="lb-color-inputs">
                  <input type="color" [(ngModel)]="module.accent" />
                  <input class="lb-color-text" [(ngModel)]="module.accent" placeholder="inherit page accent" />
                </span>
              </label>
              <div class="lb-field-row">
                <label class="lb-field">
                  <span>Card size</span>
                  <select [(ngModel)]="module.size">
                    <option [ngValue]="undefined">Normal</option>
                    <option [ngValue]="'wide'">Wide (2 columns)</option>
                    <option [ngValue]="'full'">Full width</option>
                  </select>
                </label>
                <label class="lb-field">
                  <span>Min height <em>(px, empty = auto)</em></span>
                  <input type="number" min="0" step="10" [(ngModel)]="module.minHeight" placeholder="auto" />
                </label>
              </div>

              <div class="lb-links-heading-row">
                <span class="lb-links-heading">Buttons</span>
                <button type="button" mat-stroked-button class="lb-button lb-small-button" (click)="addLink(module)">
                  <mat-icon>add</mat-icon> Add button
                </button>
              </div>

              @for (link of module.links; track link.id; let li = $index) {
                <div class="lb-link-group">
                  <div class="lb-link-row">
                    <input class="lb-link-label" [(ngModel)]="link.label" [placeholder]="link.children?.length ? 'Group label' : 'Button label'" />
                    @if (!link.children?.length) {
                      <select class="lb-link-type" [(ngModel)]="link.targetType" matTooltip="Where this button navigates">
                        <option value="dashboard">Dashboard</option>
                        <option value="route">App route</option>
                        <option value="url">External URL</option>
                      </select>
                      @if (link.targetType === 'dashboard' && dashboardOptions.length) {
                        <select class="lb-link-target" [(ngModel)]="link.target">
                          <option value="">— pick a dashboard —</option>
                          @for (option of dashboardOptions; track option.slug) {
                            <option [value]="option.slug">{{ option.label }}</option>
                          }
                        </select>
                      } @else {
                        <input class="lb-link-target" [(ngModel)]="link.target" [placeholder]="targetPlaceholder(link)" />
                      }
                      <select class="lb-link-style" [(ngModel)]="link.style" matTooltip="Button style">
                        <option value="outline">Outline</option>
                        <option value="primary">Filled</option>
                      </select>
                    } @else {
                      <!-- A button with children is a pure expand/collapse group —
                           its own target is ignored, so hide those controls. -->
                      <span class="lb-link-groupnote">Sub-menu · {{ link.children!.length }} item(s)</span>
                    }
                    <button type="button" mat-icon-button (click)="addChildLink(link)" matTooltip="Add a sub-menu item under this button">
                      <mat-icon>playlist_add</mat-icon>
                    </button>
                    <button type="button" mat-icon-button class="lb-danger" (click)="removeLink(module, li)" matTooltip="Remove button (and its sub-menu)">
                      <mat-icon>close</mat-icon>
                    </button>
                  </div>

                  @for (child of link.children ?? []; track child.id; let ci = $index) {
                    <div class="lb-link-row lb-sublink-row">
                      <mat-icon class="lb-sublink-indent">subdirectory_arrow_right</mat-icon>
                      <input class="lb-link-label" [(ngModel)]="child.label" placeholder="Sub item label" />
                      <select class="lb-link-type" [(ngModel)]="child.targetType" matTooltip="Where this sub item navigates">
                        <option value="dashboard">Dashboard</option>
                        <option value="route">App route</option>
                        <option value="url">External URL</option>
                      </select>
                      @if (child.targetType === 'dashboard' && dashboardOptions.length) {
                        <select class="lb-link-target" [(ngModel)]="child.target">
                          <option value="">— pick a dashboard —</option>
                          @for (option of dashboardOptions; track option.slug) {
                            <option [value]="option.slug">{{ option.label }}</option>
                          }
                        </select>
                      } @else {
                        <input class="lb-link-target" [(ngModel)]="child.target" [placeholder]="targetPlaceholder(child)" />
                      }
                      <button type="button" mat-icon-button class="lb-danger" (click)="removeChildLink(link, ci)" matTooltip="Remove sub item">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                  }
                </div>
              }
            </details>
          }
        </section>
      </aside>

      <!-- Right: live preview (clicking a card focuses its form section) -->
      <section class="lb-preview">
        <div class="lb-preview-banner">Live preview — click a module card to edit it</div>
        <app-dvm-landing-page [config]="cfg" [interactive]="false" (moduleSelect)="onPreviewModuleSelect($event)"></app-dvm-landing-page>
      </section>
    </div>
  }
</div>
